import { useState } from 'react';
import { ApiError } from '../api';
import { money, utcDate } from '../format';
import type { BudgetRequestDecisionIn, BudgetRequestOut, Identity } from '../types';

interface BudgetRequestListProps {
  items: BudgetRequestOut[];
  identity: Identity | null;
  onApprove: (id: string, data: BudgetRequestDecisionIn) => Promise<void>;
  onReject: (id: string, data: BudgetRequestDecisionIn) => Promise<void>;
}

/**
 * List of budget requests (pending + active + failed). Approvers can approve/reject
 * pending requests. Active increases show when they expire. revert_failed rows are
 * highlighted for admin attention.
 */
export function BudgetRequestList({ items, identity, onApprove, onReject }: BudgetRequestListProps) {
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decidingAction, setDecidingAction] = useState<'approve' | 'reject' | null>(null);
  const [decisionReason, setDecisionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (items.length === 0) {
    return (
      <div className="empty">
        <p>No budget requests yet.</p>
      </div>
    );
  }

  const canDecide = (request: BudgetRequestOut): boolean => {
    if (!identity) return false;
    if (request.status !== 'pending') return false;
    // Cannot approve own request
    if (request.requested_by_email === identity.email) return false;
    // Platform admin can decide all
    if (identity.is_platform_admin) return true;
    // Team lead can decide their team's requests
    const teamRole = identity.teams.find((t) => t.team_id === request.team_id);
    return teamRole?.role === 'lead';
  };

  const handleDecisionStart = (id: string, action: 'approve' | 'reject') => {
    setDecidingId(id);
    setDecidingAction(action);
    setDecisionReason('');
    setError(null);
  };

  const handleDecisionSubmit = async (id: string) => {
    if (!decidingAction) return;
    setSubmitting(true);
    setError(null);
    try {
      const data: BudgetRequestDecisionIn = { reason: decisionReason.trim() || null };
      if (decidingAction === 'approve') {
        await onApprove(id, data);
      } else {
        await onReject(id, data);
      }
      setDecidingId(null);
      setDecidingAction(null);
      setDecisionReason('');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'cannot_approve_own_request') {
          setError('You cannot approve your own request.');
        } else if (err.code === 'budget_request_already_decided') {
          setError('This request has already been decided. Refresh to see the update.');
        } else if (err.code === 'over_team_budget') {
          setError('This increase would exceed the team budget.');
        } else if (err.code === 'budget_request_not_found') {
          setError('Request not found. It may have been deleted.');
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecisionCancel = () => {
    setDecidingId(null);
    setDecidingAction(null);
    setDecisionReason('');
    setError(null);
  };

  return (
    <div>
      <table className="keys">
        <caption className="sr-only">Budget increase requests</caption>
        <thead>
          <tr>
            <th scope="col">Key</th>
            <th scope="col">Team</th>
            <th scope="col">Increase</th>
            <th scope="col">Period</th>
            <th scope="col">Status</th>
            <th scope="col">Requested by</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((req) => {
            const deciding = decidingId === req.id;
            const showDecisionUI = deciding && canDecide(req);

            return (
              <tr key={req.id} className={req.status === 'revert_failed' ? 'row-warning' : undefined}>
                <td>
                  <div className="key-name">{req.key_name ?? req.key_id}</div>
                  {req.reason && (
                    <div className="key-label muted" style={{ fontSize: '12px', marginTop: '2px' }}>
                      {req.reason}
                    </div>
                  )}
                </td>
                <td>{req.team_name ?? req.team_id}</td>
                <td className="numeric">
                  <div>
                    {req.baseline_limit_usd !== null
                      ? `${money(req.baseline_limit_usd)} → ${money((req.baseline_limit_usd ?? 0) + req.delta_usd)}`
                      : `+${money(req.delta_usd)}`}
                  </div>
                  {req.applied_limit_usd !== null && (
                    <div className="muted" style={{ fontSize: '11px' }}>
                      Applied: {money(req.applied_limit_usd)}
                    </div>
                  )}
                </td>
                <td className="numeric">
                  <div>{req.granularity}</div>
                  {req.effective_expires_at && (
                    <div className="muted" style={{ fontSize: '11px' }}>
                      Expires {utcDate(req.effective_expires_at)}
                    </div>
                  )}
                </td>
                <td>
                  <StatusBadge status={req.status} />
                  {req.status === 'revert_failed' && req.revert_error && (
                    <div className="callout callout-warning" style={{ marginTop: '4px', fontSize: '11px' }}>
                      <strong>Revert failed:</strong> {req.revert_error}
                    </div>
                  )}
                </td>
                <td>
                  <div>{req.requested_by_email ?? 'unknown'}</div>
                  {req.decided_by_email && (
                    <div className="muted" style={{ fontSize: '11px' }}>
                      Decided by {req.decided_by_email}
                    </div>
                  )}
                </td>
                <td>
                  {showDecisionUI ? (
                    <div className="key-action-form" style={{ minWidth: '200px' }}>
                      <label htmlFor={`reason-${req.id}`} style={{ fontSize: '11px' }}>
                        Reason (optional)
                      </label>
                      <textarea
                        id={`reason-${req.id}`}
                        value={decisionReason}
                        onChange={(e) => setDecisionReason(e.target.value)}
                        disabled={submitting}
                        placeholder="Optional note"
                        rows={2}
                        style={{ fontSize: '12px', width: '100%', marginBottom: '4px' }}
                      />
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className={decidingAction === 'approve' ? 'primary' : 'danger'}
                          onClick={() => handleDecisionSubmit(req.id)}
                          disabled={submitting}
                          style={{ fontSize: '12px', flex: 1 }}
                        >
                          {submitting
                            ? 'Submitting...'
                            : decidingAction === 'approve'
                              ? 'Confirm approve'
                              : 'Confirm reject'}
                        </button>
                        <button
                          onClick={handleDecisionCancel}
                          disabled={submitting}
                          style={{ fontSize: '12px' }}
                        >
                          Cancel
                        </button>
                      </div>
                      {error && (
                        <div className="action-error" style={{ marginTop: '4px' }}>
                          {error}
                        </div>
                      )}
                    </div>
                  ) : canDecide(req) ? (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        className="primary"
                        onClick={() => handleDecisionStart(req.id, 'approve')}
                        style={{ fontSize: '12px' }}
                      >
                        Approve
                      </button>
                      <button
                        className="danger"
                        onClick={() => handleDecisionStart(req.id, 'reject')}
                        style={{ fontSize: '12px' }}
                      >
                        Reject
                      </button>
                    </div>
                  ) : req.status === 'pending' && req.requested_by_email === identity?.email ? (
                    <span className="muted" style={{ fontSize: '12px' }}>
                      Awaiting approval
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  let className = 'badge';
  if (status === 'active') {
    className = 'badge badge-ok';
  } else if (status === 'pending') {
    className = 'badge badge-pending';
  } else if (status === 'rejected') {
    className = 'badge badge-blocked';
  } else if (status === 'revert_failed') {
    className = 'badge badge-warning';
  } else if (status === 'reverted' || status === 'superseded') {
    className = 'badge badge-muted';
  }
  return <span className={className}>{status}</span>;
}
